import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Table, Row, Col, Badge, Spinner, Container, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import axios from "axios";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";
import { FaArrowLeft, FaEdit, FaTrash } from "react-icons/fa";

const BomPumpViewPage = () => {
  const { pointindex, id } = useParams();
  const navigate = useNavigate();
  const [bomData, setBomData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBomData = async () => {
      try {
        const response = await axios.get(`${BASE_URL}bom/${id}`, {
          headers: authHeader(),
        });
        setBomData(response.data);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch Pump BOM data.",
        });
        navigate(-1);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBomData();
  }, [id, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN');
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value || 0);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Open': return 'success';
      case 'Close': return 'secondary';
      case 'Draft': return 'warning';
      default: return 'info';
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!bomData) {
    return <div className="alert alert-danger text-center mt-4">Pump BOM data not found</div>;
  }

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${BASE_URL}bom/${id}`, {
          headers: authHeader(),
        });
        navigate(`/bomindex/${pointindex}`);
        Swal.fire('Deleted!', 'BOM deleted successfully.', 'success');
      } catch (error) {
        Swal.fire('Error', error.response?.data || 'An error occurred', 'error');
      }
    }
  };

  return (
    <Container fluid className="p-3">
      {/* Header Section */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold text-primary mb-0">PUMP BOM - VIEW</h5>
            <div>
              <Button variant="outline-danger" size="sm" className="me-2" onClick={() => handleDelete(id)}>
                <FaTrash className="me-1" />
                Delete
              </Button>
              <Link to={`/BOMEdit/${id}/${pointindex}`} className="btn btn-outline-warning btn-sm me-2">
                <FaEdit className="me-1" />
                Edit
              </Link>
              <Link to={`/bomindex/${pointindex}`} className="btn btn-secondary btn-sm">
                <FaArrowLeft className="me-1" />
                Back
              </Link>
            </div>
          </div>

          <Row className="g-2">
            <Col md={3}>
              <div className="border-start border-3 border-primary ps-2">
                <small className="text-muted">BOM Type</small>
                <div className="fw-bold">{bomData.bomtype || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-success ps-2">
                <small className="text-muted">Product Name</small>
                <div className="fw-bold">{bomData.finalfinishedproduct?.description || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-warning ps-2">
                <small className="text-muted">Part No</small>
                <div className="fw-bold">{bomData.finalfinishedproduct?.partno || '-'}</div>
              </div>

            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-warning ps-2">
                <small className="text-muted">Size</small>
                <div className="fw-bold">{bomData.finalfinishedproduct?.size || '-'}</div>
              </div>

            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-info ps-2">
                <small className="text-muted">Status</small>
                <div>
                  <Badge bg={getStatusBadge(bomData.status)} className="px-2 py-1">
                    {bomData.status || 'Close'}
                  </Badge>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* BOM Details */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">BOM Details</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <small className="text-muted">BOM ID</small>
              <div className="fw-bold">{bomData.id || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Job No</small>
              <div className="fw-bold">{bomData.jobnumber || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Product Name (Finished)</small>
              <div className="fw-bold">{bomData.finalfinishedproduct?.description || '-'}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Raw/Semi/Service Items Table */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Raw / Semi / Service Products</h6>
        </div>
        <div className="p-0">
          <div className="table-responsive">
            <Table bordered size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th className="text-center fw-bold py-2">#</th>
                  <th className="fw-bold py-2">Description</th>
                  <th className="fw-bold py-2">Part No</th>
                  <th className="fw-bold py-2">MOC</th>
                  <th className="fw-bold py-2">Size</th>
                  <th className="fw-bold py-2 text-center">Qty</th>
                  <th className="fw-bold py-2 text-center">UOM</th>
                  <th className="fw-bold py-2 text-end">Rate</th>
                  <th className="fw-bold py-2 text-end">Amount</th>
                </tr>
              </thead>
              <tbody>
                {bomData.bomPumpPartsItemsRawSemiService?.map((item, index) => (
                  <tr key={index}>
                    <td className="text-center">{index + 1}</td>
                    <td className="fw-semibold">{item.description || '-'}</td>
                    <td>{item.partno || '-'}</td>
                    <td>{item.moc || '-'}</td>
                    <td>{item.size || '-'}</td>
                    <td className="text-center">{item.pumprequiredquantity || '0'}</td>
                    <td className="text-center">{item.uom || '-'}</td>
                    <td className="text-end">{formatCurrency(item.rate)}</td>
                    <td className="text-end">{formatCurrency(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-light">
                <tr>
                  <td colSpan="8" className="text-end fw-bold py-2">
                    Total:
                  </td>
                  <td className="text-end fw-bold py-2">
                    {formatCurrency(bomData.pumpbomrawmaterialtotalcost)}
                  </td>
                </tr>
              </tfoot>
            </Table>
          </div>
        </div>
      </div>

      {/* Calculation Summary */}
      <Row className="mb-3">
        <Col lg={6} className="ms-auto">
          <div className="shadow-sm border rounded bg-white">
            <div className="bg-light py-2 px-3 border-bottom">
              <h6 className="mb-0 fw-bold text-center">Amount Calculation</h6>
            </div>
            <div className="p-3">
              <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                <span className="fw-semibold">Net Amount:</span>
                <span className="fw-bold">{formatCurrency(bomData.pumpbomrawmaterialtotalcost)}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                <span className="fw-semibold">Labour Charges:</span>
                <span className="fw-bold">{formatCurrency(bomData.labourcharges)}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center py-2 border-top">
                <span className="fw-bold">Grand Total</span>
                <span className="fw-bold text-primary">{formatCurrency(bomData.grandtotalpump)}</span>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Audit Information */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Audit Information</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <small className="text-muted">Created By</small>
              <div className="fw-bold">{bomData.createbyname || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Created Date</small>
              <div className="fw-bold">{formatDate(bomData.createddate)}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Created Time</small>
              <div className="fw-bold">{bomData.createdtime || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Updated By</small>
              <div className="fw-bold">{bomData.updatedbyname || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Updated Date</small>
              <div className="fw-bold">{formatDate(bomData.updateddate)}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Updated Time</small>
              <div className="fw-bold">{bomData.updatedtime || '-'}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="text-center mt-3 pt-2 border-top">
        <Link to={`/BOMEdit/${id}/${pointindex}`} className="btn btn-primary btn-sm me-2">
          <FaEdit className="me-1" />
          Edit BOM
        </Link>
        <Link to={`/bomindex/${pointindex}`} className="btn btn-secondary btn-sm">
          <FaArrowLeft className="me-1" />
          Back to List
        </Link>
      </div>
    </Container>
  );
};

export default BomPumpViewPage;